import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, PlusCircle, Trash2, Pencil, LogOut, Megaphone, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import { OrganiserFeeds } from "@/components/OrganiserFeeds";
import { useAuth } from "@/context/AuthContext";
import { useCities } from "@/context/CitiesContext";
import { useCategories } from "@/context/CategoriesContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const inputCls =
  "w-full bg-black/40 border border-white/10 px-3.5 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-ec-volt transition-colors";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

const emptyForm = { title: "", sport: "", team: "", venue: "", start_at: "", end_at: "", description: "", ticket_url: "", city: "" };

function fmt(iso) {
  return new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function OrganiserPortal() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const { cities: allCities } = useCities();
  const { sports, teams: allOrgs, sportLabel } = useCategories();
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (!loading && user && user.role !== "organiser" && user.role !== "admin") navigate("/");
  }, [user, loading, navigate]);

  const myCities = useMemo(() => {
    const slugs = user?.cities || [];
    if (!slugs.length) return allCities;
    return allCities.filter((c) => slugs.includes(c.slug));
  }, [user, allCities]);

  const load = () => {
    setBusy(true);
    api.get("/organiser/events").then(({ data }) => setEvents(data)).finally(() => setBusy(false));
  };
  useEffect(() => { if (user) load(); }, [user]);

  useEffect(() => {
    if (!form.city && myCities.length) setForm((f) => ({ ...f, city: myCities[0].slug }));
  }, [myCities]); // eslint-disable-line

  const teams = useMemo(() => (form.sport ? allOrgs.filter((t) => t.sport === form.sport) : []), [form.sport, allOrgs]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      title: ev.title, sport: ev.sport, team: ev.team || "", venue: ev.venue,
      start_at: toLocalInput(ev.start_at), end_at: toLocalInput(ev.end_at),
      description: ev.description || "", ticket_url: ev.ticket_url || "", city: ev.city,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditingId(null); setForm({ ...emptyForm, city: myCities[0]?.slug || "" }); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.sport || !form.venue || !form.start_at || !form.city) {
      toast.error("Fill title, category, venue, start and city");
      return;
    }
    setSaving(true);
    const payload = {
      ...form, team: form.team || null,
      start_at: new Date(form.start_at).toISOString(),
      end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
    };
    try {
      if (editingId) {
        await api.put(`/organiser/events/${editingId}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/organiser/events", payload);
        toast.success("Event published");
      }
      cancelEdit();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/organiser/events/${id}`);
      toast.success("Event deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (loading || !user)
    return (
      <div className="App">
        <Navbar />
        <p className="text-center text-zinc-500 overline py-24">Checking access…</p>
      </div>
    );

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="overline text-ec-volt mb-1 flex items-center gap-2"><Megaphone size={14} /> Organiser Portal</p>
            <h1 className="font-heading text-5xl tracking-tight leading-none">{user.name}</h1>
            <p className="text-zinc-400 text-sm mt-2">
              Your events publish instantly to: {myCities.map((c) => c.name).join(", ") || "—"}
            </p>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="portal-logout" className="inline-flex items-center gap-2 overline border border-white/15 px-4 py-2.5 hover:border-ec-red hover:text-ec-red transition-colors">
            <LogOut size={15} /> Logout
          </button>
        </div>

        <OrganiserFeeds myCities={myCities} onSynced={load} />

        <form onSubmit={submit} className="bg-ec-surface border border-white/10 p-6 space-y-5 mb-10" data-testid="portal-event-form">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-3xl tracking-wide">{editingId ? "Edit Event" : "Add Event"}</h2>
            {editingId && (
              <button type="button" onClick={cancelEdit} data-testid="portal-cancel-edit" className="inline-flex items-center gap-1.5 overline text-zinc-400 hover:text-ec-red"><X size={14} /> Cancel</button>
            )}
          </div>
          <label className="block"><span className="overline text-zinc-500 mb-2 block">Title *</span>
            <input data-testid="portal-title" value={form.title} onChange={set("title")} className={inputCls} /></label>
          <div className="grid sm:grid-cols-2 gap-5">
            <label className="block"><span className="overline text-zinc-500 mb-2 block">City *</span>
              <select data-testid="portal-city" value={form.city} onChange={set("city")} className={inputCls}>
                {myCities.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select></label>
            <label className="block"><span className="overline text-zinc-500 mb-2 block">Category *</span>
              <select data-testid="portal-sport" value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value, team: "" })} className={inputCls}>
                <option value="">Select</option>{sports.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select></label>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <label className="block"><span className="overline text-zinc-500 mb-2 block">Organiser / Team</span>
              <select data-testid="portal-team" value={form.team} onChange={set("team")} className={inputCls} disabled={!form.sport}>
                <option value="">Optional</option>{teams.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select></label>
            <label className="block"><span className="overline text-zinc-500 mb-2 block">Venue *</span>
              <input data-testid="portal-venue" value={form.venue} onChange={set("venue")} className={inputCls} /></label>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <label className="block"><span className="overline text-zinc-500 mb-2 block">Start *</span>
              <input data-testid="portal-start" type="datetime-local" value={form.start_at} onChange={set("start_at")} className={inputCls} /></label>
            <label className="block"><span className="overline text-zinc-500 mb-2 block">End</span>
              <input data-testid="portal-end" type="datetime-local" value={form.end_at} onChange={set("end_at")} className={inputCls} /></label>
          </div>
          <label className="block"><span className="overline text-zinc-500 mb-2 block">Ticket / Info Link</span>
            <input data-testid="portal-ticket" type="url" value={form.ticket_url} onChange={set("ticket_url")} className={inputCls} placeholder="https://…" /></label>
          <label className="block"><span className="overline text-zinc-500 mb-2 block">Description</span>
            <textarea data-testid="portal-description" rows={3} value={form.description} onChange={set("description")} className={inputCls} /></label>
          <button type="submit" disabled={saving} data-testid="portal-submit" className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-7 py-3.5 hover:-translate-y-0.5 transition-all disabled:opacity-50">
            <PlusCircle size={16} /> {saving ? "Saving…" : editingId ? "Update Event" : "Publish Event"}
          </button>
        </form>

        <h2 className="font-heading text-3xl tracking-wide mb-4">Your Events</h2>
        {busy ? (
          <p className="overline text-zinc-500">Loading…</p>
        ) : !events.length ? (
          <div className="border border-white/10 bg-ec-surface p-12 text-center">
            <p className="font-heading text-3xl text-zinc-400">No events yet</p>
            <p className="text-zinc-500 mt-2">Add your first fixture above — it goes live straight away.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="portal-events-list">
            {events.map((e) => (
              <div key={e.id} data-testid={`portal-event-${e.id}`} className="bg-ec-surface border border-white/10 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="overline bg-white/10 px-2 py-0.5 text-ec-volt">{sportLabel(e.sport)}</span>
                    <span className="overline bg-white/10 px-2 py-0.5 text-zinc-400 capitalize">{e.city}</span>
                  </div>
                  <h3 className="font-heading text-2xl tracking-wide leading-none mt-2">{e.title}</h3>
                  <p className="text-sm text-zinc-400 mt-1 flex items-center gap-2"><CalendarClock size={14} /> {fmt(e.start_at)} · {e.venue}</p>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`portal-edit-${e.id}`} onClick={() => startEdit(e)} className="inline-flex items-center gap-1.5 overline border border-white/15 px-4 py-2.5 text-zinc-300 hover:border-ec-volt hover:text-ec-volt transition-colors"><Pencil size={15} /> Edit</button>
                  <button data-testid={`portal-delete-${e.id}`} onClick={() => del(e.id)} className="inline-flex items-center gap-1.5 overline border border-white/15 px-4 py-2.5 text-zinc-300 hover:border-ec-red hover:text-ec-red transition-colors"><Trash2 size={15} /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
